/**
 * Currency Formatter Pipe
 * 
 * Formats numeric values as currency strings with appropriate separators and prefixes
 * based on the application's currency configuration.
 * 
 * @author Ninad Kulkarni
 * @date 25-9-2017
 */

import { Pipe, PipeTransform } from '@angular/core';
import { AppMetadataHandler } from '../../../configs/app-metadata.handler';

@Pipe({ 
  name: 'CurrencyFormatter', 
  pure: false 
})
export class CurrencyFormatterPipe implements PipeTransform {
  private PREFIX: string;
  private DECIMAL_SEPARATOR: string;
  private THOUSANDS_SEPARATOR: string;
  private PADDING: string = '';

  constructor(public appMetadataHandler: AppMetadataHandler) {
    this.PREFIX = this.appMetadataHandler.currencyOptions.prefix + ' ';
    this.DECIMAL_SEPARATOR = String(this.appMetadataHandler.currencyOptions.decimal);
    this.THOUSANDS_SEPARATOR = String(this.appMetadataHandler.currencyOptions.thousands);
  }
  
  /**
   * Transforms a numeric value into a formatted currency string
   * 
   * @param value - The numeric value to format
   * @param currencyCode - Optional currency code to use specific formatting
   * @param fractionSize - Number of decimal places to display
   * @returns Formatted currency string
   */
  transform(value: string | number, currencyCode?: string, fractionSize: number = 2): string {
    const num: string = !value || isNaN(Number(value)) 
      ? '0' 
      : Number(value).toFixed(fractionSize);
    
    // Apply currency-specific formatting if currency code is provided
    if (currencyCode) {
      const currencyObj = this.appMetadataHandler.supportedCurrencyMaster[currencyCode];
      if (currencyObj) {
        // Update local formatting variables
        this.PREFIX = currencyObj.baseCurrencyPREFIX + ' ';
        this.DECIMAL_SEPARATOR = String(currencyObj.DECIMAL_SEPARATOR);
        this.THOUSANDS_SEPARATOR = String(currencyObj.THOUSANDS_SEPARATOR);
      }
    }
    
    const [integer, fraction = ''] = num.split('.');
    const formattedFraction = Number(num) > 0 && fractionSize > 0 
      ? this.DECIMAL_SEPARATOR + (fraction + this.PADDING).substring(0, fractionSize) 
      : '';
    const formattedInteger = integer.replace(/\B(?=(\d{3})+(?!\d))/g, this.THOUSANDS_SEPARATOR);
    
    return this.PREFIX + formattedInteger + formattedFraction;
  }
}